import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  getOrganizations, getUsers, inviteUser, updateUserRole, 
  deactivateUser, getAuditLogs, deleteUpload, getUploads 
} from '../services/api';
import { 
  Building2, Users, Shield, ScrollText, UserPlus, Trash2, 
  ChevronDown, CheckCircle2, XCircle, Clock, AlertTriangle
} from 'lucide-react';

const ROLES = [
  { value: 'org_admin', label: 'Org Admin', color: 'bg-violet-100 text-violet-700' },
  { value: 'pharmacy_director', label: 'Pharmacy Director', color: 'bg-blue-100 text-blue-700' },
  { value: 'cfo', label: 'CFO', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'procurement_lead', label: 'Procurement Lead', color: 'bg-amber-100 text-amber-700' },
  { value: 'clinical_reviewer', label: 'Clinical Reviewer', color: 'bg-pink-100 text-pink-700' },
  { value: 'analyst', label: 'Analyst', color: 'bg-slate-100 text-slate-700' },
  { value: 'viewer', label: 'Viewer', color: 'bg-gray-100 text-gray-600' },
];

function RoleBadge({ role }) {
  const r = ROLES.find(r => r.value === role) || { label: role, color: 'bg-gray-100 text-gray-600' };
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${r.color}`}>{r.label}</span>;
}

function OrgCard({ org }) {
  return (
    <Card className="hover:shadow-md transition-shadow" data-testid="org-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0A2540] rounded-xl flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-base">{org.name}</CardTitle>
              <p className="text-xs text-slate-500">{org.id}</p>
            </div>
          </div>
          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${org.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
            {org.status}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-4 gap-3 text-center">
          <div><p className="text-lg font-bold text-slate-900">{org.user_count || 0}</p><p className="text-[10px] text-slate-500">Users</p></div>
          <div><p className="text-lg font-bold text-slate-900">{org.upload_count || 0}</p><p className="text-[10px] text-slate-500">Uploads</p></div>
          <div><p className="text-lg font-bold text-slate-900">{org.type}</p><p className="text-[10px] text-slate-500">Type</p></div>
          <div><p className="text-lg font-bold text-slate-900">{org.size}</p><p className="text-[10px] text-slate-500">Size</p></div>
        </div>
      </CardContent>
    </Card>
  );
}

function InviteModal({ onClose, onInvite }) {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('analyst');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await inviteUser({ email, username: username.trim().toLowerCase(), name, role });
      toast.success(`User invited! Temp password: ${res.data.temp_password}`, { duration: 10000 });
      onInvite(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" data-testid="invite-modal">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
        <h3 className="text-lg font-bold text-slate-900 mb-4">Invite New User</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Full Name</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Dr. Jane Smith" required data-testid="invite-name" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Email</label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@hospital.org" required data-testid="invite-email" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Username</label>
            <Input value={username} onChange={e => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))} placeholder="jane_doe" required minLength={3} maxLength={32} data-testid="invite-username" />
            <p className="text-[10px] text-slate-500 mt-1">3–32 characters: letters, numbers, underscore</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Role</label>
            <select value={role} onChange={e => setRole(e.target.value)} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white" data-testid="invite-role">
              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="invite-submit">{submitting ? 'Inviting...' : 'Send Invite'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminConsolePage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('users');
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [roleEdit, setRoleEdit] = useState(null);

  useEffect(() => {
    Promise.allSettled([
      getOrganizations().then(r => setOrgs(r.data.organizations || [])),
      getUsers().then(r => setUsers(r.data.users || [])),
      getAuditLogs(50).then(r => setLogs(r.data.logs || [])),
      getUploads().then(r => setUploads(r.data.uploads || [])),
    ]);
  }, []);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateUserRole(userId, newRole);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      setRoleEdit(null);
      toast.success('Role updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleDeactivate = async (userId) => {
    if (!window.confirm('Deactivate this user?')) return;
    try {
      await deactivateUser(userId);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, status: 'deactivated' } : u));
      toast.success('User deactivated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to deactivate');
    }
  };

  const handleDeleteUpload = async (uploadId) => {
    if (!window.confirm('Permanently delete this upload and all associated analysis?')) return;
    try {
      await deleteUpload(uploadId);
      setUploads(prev => prev.filter(u => u.id !== uploadId));
      toast.success('Upload deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const tabs = [
    { key: 'users', label: 'Users', icon: Users },
    { key: 'org', label: 'Organization', icon: Building2 },
    { key: 'audit', label: 'Audit Trail', icon: ScrollText },
    { key: 'data', label: 'Data Management', icon: Shield },
  ];

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-console">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Admin Console</h1>
            <p className="text-sm text-slate-500">Manage your organization, users, and data</p>
          </div>
          {tab === 'users' && (
            <Button onClick={() => setShowInvite(true)} data-testid="invite-user-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Invite User
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1" data-testid="admin-tabs">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid={`tab-${t.key}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Users Tab */}
        {tab === 'users' && (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase">User</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase">Role</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase">Status</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase">Joined</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-slate-600 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id} className="border-b last:border-0 hover:bg-slate-50/50" data-testid={`user-row-${u.id}`}>
                        <td className="px-5 py-3">
                          <p className="text-sm font-semibold text-slate-900">{u.name}</p>
                          <p className="text-xs text-slate-500">{u.email}</p>
                          {u.username && <p className="text-xs text-slate-400">@{u.username}</p>}
                        </td>
                        <td className="px-5 py-3">
                          {roleEdit === u.id ? (
                            <select autoFocus value={u.role} onChange={e => handleRoleChange(u.id, e.target.value)} onBlur={() => setRoleEdit(null)}
                              className="text-xs border rounded px-2 py-1 bg-white">
                              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                            </select>
                          ) : (
                            <button onClick={() => setRoleEdit(u.id)} className="flex items-center gap-1 group">
                              <RoleBadge role={u.role} />
                              <ChevronDown className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </button>
                          )}
                        </td>
                        <td className="px-5 py-3">
                          {u.status === 'deactivated' ? (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600">Deactivated</span>
                          ) : u.is_platform_admin ? (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">Platform Admin</span>
                          ) : (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Active</span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-xs text-slate-500">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-5 py-3 text-right">
                          {!u.is_platform_admin && u.status !== 'deactivated' && (
                            <Button variant="ghost" size="sm" onClick={() => handleDeactivate(u.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                              <XCircle className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Organization Tab */}
        {tab === 'org' && (
          <div className="space-y-4">
            {orgs.map(org => <OrgCard key={org.id} org={org} />)}
          </div>
        )}

        {/* Audit Trail Tab */}
        {tab === 'audit' && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#0A2540]" />
                <CardTitle className="text-base">Immutable Audit Trail</CardTitle>
              </div>
              <p className="text-xs text-slate-500">All actions are permanently recorded. Entries cannot be modified or deleted.</p>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-[500px] overflow-y-auto">
                <table className="w-full">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b">
                      <th className="text-left px-5 py-2 text-[10px] font-semibold text-slate-500 uppercase">Timestamp</th>
                      <th className="text-left px-5 py-2 text-[10px] font-semibold text-slate-500 uppercase">User</th>
                      <th className="text-left px-5 py-2 text-[10px] font-semibold text-slate-500 uppercase">Action</th>
                      <th className="text-left px-5 py-2 text-[10px] font-semibold text-slate-500 uppercase">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, i) => (
                      <tr key={i} className="border-b last:border-0 hover:bg-slate-50/50" data-testid={`audit-row-${i}`}>
                        <td className="px-5 py-2 text-xs text-slate-500 whitespace-nowrap">
                          {log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}
                        </td>
                        <td className="px-5 py-2">
                          <p className="text-xs font-medium text-slate-700">{log.user_name || '—'}</p>
                          <p className="text-[10px] text-slate-400">{log.user_role || ''}</p>
                        </td>
                        <td className="px-5 py-2">
                          <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-100 text-slate-700">{log.action}</span>
                        </td>
                        <td className="px-5 py-2 text-xs text-slate-500 max-w-xs truncate">
                          {JSON.stringify(log.details || {}).slice(0, 80)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Data Management Tab */}
        {tab === 'data' && (
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Upload History & Retention</CardTitle>
                <p className="text-xs text-slate-500">Manage uploaded data. Deletions are permanent and audited.</p>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left px-5 py-2 text-[10px] font-semibold text-slate-500 uppercase">File</th>
                        <th className="text-left px-5 py-2 text-[10px] font-semibold text-slate-500 uppercase">Rows</th>
                        <th className="text-left px-5 py-2 text-[10px] font-semibold text-slate-500 uppercase">Status</th>
                        <th className="text-left px-5 py-2 text-[10px] font-semibold text-slate-500 uppercase">Date</th>
                        <th className="text-right px-5 py-2 text-[10px] font-semibold text-slate-500 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploads.map(u => (
                        <tr key={u.id} className="border-b last:border-0 hover:bg-slate-50/50">
                          <td className="px-5 py-2 text-sm font-medium text-slate-900">{u.filename}</td>
                          <td className="px-5 py-2 text-xs text-slate-600">{u.row_count || '—'}</td>
                          <td className="px-5 py-2">
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{u.status}</span>
                          </td>
                          <td className="px-5 py-2 text-xs text-slate-500">{u.uploaded_at ? new Date(u.uploaded_at).toLocaleDateString() : '—'}</td>
                          <td className="px-5 py-2 text-right">
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteUpload(u.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {uploads.length === 0 && (
                        <tr><td colSpan="5" className="px-5 py-8 text-center text-sm text-slate-400">No uploads yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-bold text-amber-900">Data Retention Policy</h4>
                    <p className="text-xs text-amber-700 mt-1">
                      Uploaded files are retained for 365 days by default. Deleting an upload permanently removes the file, 
                      all parsed data, and associated analysis. This action is irreversible and will be recorded in the audit trail.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {showInvite && <InviteModal onClose={() => setShowInvite(false)} onInvite={(u) => setUsers(prev => [...prev, u])} />}
      </div>
    </Layout>
  );
}
